import { useState } from 'react';
import { Modal } from 'src/components/Modal';
import { Button } from 'src/v2/commonComponents/Button';
import classNames from 'classnames';
import { Icon, IconName } from 'src/components/Icon';
import { SVG_SIZE_L } from 'src/constants';
import { TextareaSimple } from '../TextareaSimple';
import styles from './FeedbackModal.module.scss';

export const FEEDBACK_MAP: { rating: number; title: string; icon: IconName }[] =
  [
    {
      rating: 1,
      title: 'Not useful',
      icon: 'sentiment-very-dissatisfied',
    },
    {
      rating: 2,
      title: '',
      icon: 'sentiment-dissatisfied',
    },
    {
      rating: 3,
      title: '',
      icon: 'sentiment-neutral',
    },
    {
      rating: 4,
      title: '',
      icon: 'sentiment-satisfied',
    },
    {
      rating: 5,
      title: 'Extreme useful',
      icon: 'sentiment-very-satisfied',
    },
  ];

export type FeedbackSubmitParams = {
  rating: number;
  feedback: string;
};

type FeedbackModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (value: FeedbackSubmitParams) => void;
};

export const FeedbackModal = ({
  isOpen,
  isLoading,
  onClose,
  onSubmit,
}: FeedbackModalProps) => {
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');

  const handleSubmit = () => {
    if (selectedRating === 0) {
      return;
    }

    onSubmit({
      rating: selectedRating,
      feedback,
    });

    onClose();
    setSelectedRating(0);
    setFeedback('');
  };

  const handleSelectRating = (value: number) => {
    setSelectedRating(value);
  };

  const handleClose = () => {
    onClose();
    setSelectedRating(0);
    setFeedback('');
  };

  const isDisabledSubmit = selectedRating === 0 || isLoading;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      overlayClassName={styles.overlay}
    >
      <h5 className={styles.title}>Help us improve Ninja Editor!</h5>

      <div className={styles.ratingWrapper}>
        {FEEDBACK_MAP.map(({ rating, icon, title }) => {
          const isSelected = Number(selectedRating) === rating;

          return (
            <Button
              color="transparent"
              onClick={() => handleSelectRating(rating)}
              className={classNames(styles.ratingButton, {
                [styles.selected]: isSelected,
              })}
              key={rating}
            >
              <Icon
                weight={isSelected ? 'fill' : 'regular'}
                type={icon}
                size={SVG_SIZE_L}
              />
              <span className={styles.iconTitle}>{title}</span>
            </Button>
          );
        })}
      </div>

      <div className={styles.body}>
        <TextareaSimple
          onChange={setFeedback}
          value={feedback}
          maxRows={4}
          minRows={4}
          placeholder="Write the feedback (optional)"
        />
      </div>

      <div className={styles.buttonsWrapper}>
        <Button onClick={handleSubmit} disabled={isDisabledSubmit}>
          Submit
        </Button>
      </div>
    </Modal>
  );
};
